//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, Grid, InputLabel, TextField, Typography } from '@mui/material'
import React, { ReactElement } from 'react'

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'

import { theme } from '@react/components/theme'
import {
  assetsAndSharesToExchangeRate,
  dayNumberToDate,
  dayNumberToDateString,
  erc20WeiToCommaDecString,
  exchangeRateMaturityDayCalc,
  exchangeRateToDecimalString,
  isNumber,
  toUTCDateString,
  weiToNumber,
} from '@shared/conversions'

import { TableHeaderItem } from '@react/components/TableHeaderItem'
import { useOtChainTimestamp, useOtSelector } from '@react/redux/store'
import { EXCHANGE_RATE_ONE } from '@shared/Enums'

import { EthAddr } from '@shared/SchemaTypes'
import { PoolDynamicOverviewCTO } from '@shared/sharedTypes'

export default function DynamicSetTermExchangeRateForm(props: { poolAddr: EthAddr }): ReactElement {
  const { poolAddr } = props
  const poolCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr] as PoolDynamicOverviewCTO)

  const chainTimestamp = useOtChainTimestamp()

  const [exchangeRate, setExchangeRate] = React.useState<string>(poolCTO.exchangeRate)
  const [exchangeRateDecimalString, setExchangeRateDecimalString] = React.useState<string>(
    exchangeRateToDecimalString(poolCTO.exchangeRate)
  )

  const [assetValue, setAssetValue] = React.useState<string>(erc20WeiToCommaDecString(poolCTO.totalAssets))

  const [numberOfDays, setNumberOfDays] = React.useState<number>(0)

  const [exchangeRateAtMaturity, setExchangeRateAtMaturity] = React.useState<string>(poolCTO.exchangeRateAtMaturity)

  // const originalAnnualLinearReturn = toAnnualTermReturn(
  //   exchangeRate,
  //   poolCTO.exchangeRateAtSetDay,
  //   poolCTO.exchangeRateAtMaturity,
  //   poolCTO.exchangeRateMaturityDay
  // )

  const [annualReturn, setAnnualReturn] = React.useState<number>(
    poolCTO.interestRate ? Number(poolCTO.interestRate) : 0.0
  )

  const [exchangeRateMaturityDay, setExchangeRateMaturityDay] = React.useState<number>(
    poolCTO.exchangeRateMaturityDay > 0 ? poolCTO.exchangeRateMaturityDay : Math.floor(chainTimestamp / (24 * 60 * 60))
  )
  const [errorAmount, _setErrorAmount] = React.useState<string | undefined>(undefined)

  //  const [datePickerValue, setDatePickerValue] = React.useState<Dayjs>(dayjs(dayNumberToDate(exchangeRateMaturityDay)))

  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const datePickerValue = dayjs(dayNumberToDate(exchangeRateMaturityDay))

  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    marginLeft: '8px',
    borderRadius: '6px',
    marginTop: 2,
  }
  console.log(
    'poolCTO.exchangeRateMaturityDay  ',
    poolCTO.exchangeRateMaturityDay,
    Math.floor(chainTimestamp / (24 * 60 * 60)),
    exchangeRateMaturityDay
  )

  const setExchangeRateAction = (): void => {
    setIsSubmitted(true)
    // dispatch(
    //   repayDynamicWithdrawTx({
    //     activity,
    //     amount: commaDecStringToErc20Wei(amount),
    //     fees: commaDecStringToErc20Wei(fees),
    //   })
    // )
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }
  const disableButton = false

  const dateFormatOptions = Intl.DateTimeFormat(window.navigator?.language).formatToParts()

  const dateFormatString = dateFormatOptions
    .map((part) => {
      switch (part.type) {
        case 'day':
          return 'DD' // Day of the month (two digits)
        case 'month':
          return 'MM' // Month (two digits)
        case 'year':
          return 'YYYY' // Full year (four digits)
        default:
          return part.value // Retain other parts like separators, e.g., "/", "-"
      }
    })
    .join('')
  const locale = window.navigator?.language

  const assetsAtMaturity = (BigInt(exchangeRateAtMaturity) * BigInt(poolCTO.totalShares)) / EXCHANGE_RATE_ONE
  const newAssetsBI = (BigInt(exchangeRate) * BigInt(poolCTO.totalShares)) / EXCHANGE_RATE_ONE
  const setDayAssets = (BigInt(poolCTO.exchangeRateAtSetDay) * BigInt(poolCTO.totalShares)) / EXCHANGE_RATE_ONE
  return (
    <Grid container sx={{ marginTop: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <TableHeaderItem>Assets</TableHeaderItem>
        </Grid>
        <Grid item xs={3}>
          <TableHeaderItem>Shares</TableHeaderItem>
        </Grid>
        <Grid item xs={2}>
          <TableHeaderItem>Date</TableHeaderItem>
        </Grid>
        <Grid item xs={4}>
          <TableHeaderItem>Exchange Rate</TableHeaderItem>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableHeaderItem>At Last Exchange rate update</TableHeaderItem>
        </Grid>
      </Grid>

      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Box sx={{ width: '75%' }}>{erc20WeiToCommaDecString(setDayAssets)} </Box>
        </Grid>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {dayNumberToDateString(poolCTO.exchangeRateSetDay)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateToDecimalString(poolCTO.exchangeRateAtSetDay)}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableHeaderItem>Current values</TableHeaderItem>
        </Grid>
      </Grid>
      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Box sx={{ width: '75%' }}>{erc20WeiToCommaDecString(poolCTO.totalAssets)} </Box>
        </Grid>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {toUTCDateString(chainTimestamp)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateDecimalString}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableHeaderItem>Updated values</TableHeaderItem>
        </Grid>
      </Grid>
      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Box sx={{ width: '75%' }}>{erc20WeiToCommaDecString(newAssetsBI)} </Box>
        </Grid>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {toUTCDateString(chainTimestamp)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateDecimalString}
          </Typography>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableHeaderItem>Values At Maturity</TableHeaderItem>
        </Grid>
      </Grid>
      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Box sx={{ width: '75%' }}>{erc20WeiToCommaDecString(assetsAtMaturity)} </Box>
        </Grid>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {toUTCDateString(exchangeRateMaturityDay * 24 * 60 * 60)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateToDecimalString(exchangeRateAtMaturity)}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <InputLabel htmlFor="assetValue">Updated Asset Value</InputLabel>
        <TextField
          id="assetValue"
          inputProps={{
            'data-testid': 'assetValue',
          }}
          sx={{ width: '300px' }}
          variant="outlined"
          placeholder="Enter Exchange Rate (Decimal Form)"
          value={assetValue}
          type={'tel'}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
            setAssetValue(event.target.value)
            const sanitizedValue = event.target.value.replace(/,/g, '')
            const assetvalueNumber = Number(sanitizedValue)

            console.log('assetvalueNumber ', assetvalueNumber, event.target.value)
            const er = assetsAndSharesToExchangeRate(assetvalueNumber, poolCTO.totalShares)
            setExchangeRateDecimalString(exchangeRateToDecimalString(er))
            setExchangeRate(er.toString())
            setExchangeRateAtMaturity(exchangeRateMaturityDayCalc(er, annualReturn, numberOfDays).toString())
            // TODO            setExchangeRateChangeRate(annualReturnToExchangeRateChangeRate(annualLinearReturn, BigInt(exchangeRate)))
          }}
          onFocus={() => {
            console.log('onFocus')
          }}
          onBlur={() => {
            console.log('onBlur')
          }}
          error={!!errorAmount}
          helperText={errorAmount}
        />
      </Grid>
      <Grid item xs={12}>
        <InputLabel htmlFor="annualLinearReturn">Annual Linear Return</InputLabel>
        <TextField
          id="annualLinearReturn"
          inputProps={{
            'data-testid': 'annualLinearReturn',
            step: '0.01',
          }}
          sx={{ width: '300px' }}
          variant="outlined"
          placeholder="Enter ExchangeRate Daily change"
          value={annualReturn}
          type={'number'}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
            if (isNumber(event.target.value)) {
              const alr = Number(event.target.value)
              setAnnualReturn(alr)
              // TOTO setExchangeRateChangeRate(annualReturnToExchangeRateChangeRate(alr, BigInt(exchangeRate)))

              setExchangeRateAtMaturity(exchangeRateMaturityDayCalc(exchangeRate, alr, numberOfDays).toString())
            }
          }}
          onFocus={() => {
            console.log('onFocus')
          }}
          onBlur={() => {
            console.log('onBlur')
          }}
          error={!!errorAmount}
          helperText={errorAmount}
        />
        <Box sx={{ float: 'right' }}>
          <Typography variant={'buttonBold'}>exchangeRateAtMaturity </Typography>
          <br />
          <Typography variant={'buttonBold'}>{exchangeRateAtMaturity} </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} sx={{ marginTop: 4 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
          <DatePicker
            onChange={(newValue: Dayjs | null) => {
              if (newValue) {
                const v = newValue.format('YYYY-MM-DD ')
                const newDayNum = Math.floor(newValue.toDate().getTime() / (24 * 60 * 60 * 1000))
                console.log('newDayNum ', exchangeRateMaturityDay, ' change to ', newDayNum)
                setExchangeRateMaturityDay(newDayNum)
                const dayNum = Math.floor(chainTimestamp / (24 * 60 * 60))
                const _numberOfDays = newDayNum - dayNum
                setNumberOfDays(_numberOfDays)
                setExchangeRateAtMaturity(
                  exchangeRateMaturityDayCalc(exchangeRate, annualReturn, _numberOfDays).toString()
                )
                console.log('v ', v)
              }
            }}
            label="Maturity Date"
            format={dateFormatString}
            value={datePickerValue}
          />
        </LocalizationProvider>
        <Box sx={{ float: 'right' }}>
          <Typography variant={'buttonBold'}>Number of days </Typography>
          <br />
          <Typography variant={'buttonBold'}>{numberOfDays} </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} sm={12}>
        <Button
          disabled={isSubmitted || disableButton}
          sx={sx}
          onClick={() => {
            setExchangeRateAction()
          }}
          variant={'contained'}
        >
          <Typography variant={'buttonBold'}>Set Exchange rate</Typography>
        </Button>
      </Grid>
      {disableButton && (
        <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
          The amount you are trying to send for withdraw is greater than the amount of {poolCTO.liquidityTokenSymbol}{' '}
          available in your vault ({weiToNumber(poolCTO?.totalAssets)}). Please deposit funds to the borrowerVault
        </Typography>
      )}
    </Grid>
  )
}
